import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Loader2, Radar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type Status = "shipped" | "in-progress" | "planned";

type Milestone = {
  id: string;
  slug: string;
  title: string;
  when_label: string;
  target_date: string | null;
  status: Status;
  progress: number;
  items: unknown;
  reached_at: string | null;
  updated_at: string;
};

const STATUS_META: Record<Status, { label: string; icon: typeof Circle; className: string }> = {
  shipped: { label: "Reached", icon: CheckCircle2, className: "text-primary" },
  "in-progress": { label: "In progress", icon: Loader2, className: "text-accent" },
  planned: { label: "Planned", icon: Circle, className: "text-muted-foreground" },
};

const toItems = (value: unknown): string[] =>
  Array.isArray(value) ? value.map((v) => String(v)) : [];

const Milestones = () => {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const { data, error: dbError } = await (supabase as any)
        .from("roadmap_milestones")
        .select("*")
        .order("sort_order", { ascending: true });
      if (!active) return;
      if (dbError) setError("We couldn't load the milestones just now. Please refresh in a moment.");
      else setMilestones((data as Milestone[]) ?? []);
      setLoading(false);
    };
    void load();

    // Live updates: the page reflects a milestone the moment it is marked reached.
    const channel = (supabase as any)
      .channel(`roadmap-milestones:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "roadmap_milestones" },
        () => void load(),
      )
      .subscribe();

    return () => {
      active = false;
      (supabase as any).removeChannel(channel);
    };
  }, []);

  const reached = milestones.filter((m) => m.status === "shipped").length;
  const overall = milestones.length
    ? Math.round(milestones.reduce((sum, m) => sum + (m.progress ?? 0), 0) / milestones.length)
    : 0;

  return (
    <>
      <Helmet>
        <title>Live Milestones — Trends Discovery Progress Tracker</title>
        <meta
          name="description"
          content="Live progress on every Trends Discovery roadmap milestone, updated as each one is reached — with dates, progress and what shipped."
        />
        <link rel="canonical" href="https://trendsdiscovery.com/milestones" />
        <meta property="og:title" content="Live Milestones — Trends Discovery" />
        <meta
          property="og:description"
          content="Track progress on the Trends Discovery roadmap as each milestone is reached."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://trendsdiscovery.com/milestones" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <main className="min-h-screen bg-background text-foreground">
        <div className="container max-w-3xl py-16">
          <Link
            to="/roadmap"
            className="inline-flex items-center gap-2 font-mono text-xs text-primary hover:underline"
          >
            <ArrowLeft className="w-3 h-3" /> BACK TO ROADMAP
          </Link>

          <div className="mt-8 flex items-center gap-2">
            <span className="grid place-items-center w-7 h-7 rounded bg-primary text-primary-foreground">
              <Radar className="w-4 h-4" />
            </span>
            <span className="font-display font-semibold">Trends.Discovery</span>
          </div>

          <h1 className="mt-6 font-display text-4xl md:text-5xl font-bold">Live milestones</h1>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            This page updates itself as each roadmap item is reached — no announcements needed.
            {milestones.length > 0 && (
              <>
                {" "}
                <span className="text-foreground font-medium">
                  {reached} of {milestones.length} milestones reached
                </span>{" "}
                · {overall}% of the roadmap complete.
              </>
            )}
          </p>

          {milestones.length > 0 && (
            <div
              className="mt-6 h-2 w-full rounded-full bg-muted overflow-hidden"
              role="progressbar"
              aria-valuenow={overall}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Overall roadmap progress"
            >
              <div className="h-full bg-primary" style={{ width: `${overall}%` }} />
            </div>
          )}

          {loading && <p className="mt-12 text-sm text-muted-foreground">Loading milestones…</p>}
          {error && <p className="mt-12 text-sm text-destructive">{error}</p>}

          <ol className="mt-12 space-y-5">
            {milestones.map((m) => {
              const meta = STATUS_META[m.status] ?? STATUS_META.planned;
              const Icon = meta.icon;
              return (
                <li
                  key={m.id}
                  className="rounded-2xl border border-border bg-card p-6"
                  data-testid="milestone"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <Icon className={`w-4 h-4 ${meta.className}`} aria-hidden="true" />
                    <span className="font-mono text-xs text-primary">{m.when_label}</span>
                    <span className="text-[10px] font-mono tracking-wider rounded-full border border-border px-2 py-0.5 text-muted-foreground">
                      {meta.label.toUpperCase()}
                    </span>
                    {m.reached_at && (
                      <time
                        dateTime={m.reached_at}
                        className="font-mono text-[10px] text-muted-foreground"
                      >
                        REACHED {new Date(m.reached_at).toLocaleDateString()}
                      </time>
                    )}
                  </div>

                  <h2 className="mt-3 font-display text-2xl font-bold">{m.title}</h2>

                  <div className="mt-4 flex items-center gap-3">
                    <div
                      className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden"
                      role="progressbar"
                      aria-valuenow={m.progress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`${m.title} progress`}
                    >
                      <div className="h-full bg-primary" style={{ width: `${m.progress}%` }} />
                    </div>
                    <span className="font-mono text-xs text-muted-foreground">{m.progress}%</span>
                  </div>

                  <ul className="mt-4 space-y-2">
                    {toItems(m.items).map((item) => (
                      <li key={item} className="text-sm text-muted-foreground leading-relaxed">
                        · {item}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ol>

          {!loading && !error && milestones.length === 0 && (
            <p className="mt-12 text-sm text-muted-foreground">
              Milestones are being set up — check back shortly.
            </p>
          )}

          <div className="mt-16 rounded-2xl border border-border bg-card p-6 text-center">
            <h2 className="font-display text-2xl font-bold">Everything here is included</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              The $39 lifetime licence covers every milestone on this page. See the{" "}
              <Link to="/roadmap" className="text-primary hover:underline">
                roadmap
              </Link>{" "}
              for the wider picture or browse the{" "}
              <Link to="/marketplace" className="text-primary hover:underline">
                marketplace
              </Link>
              .
            </p>
          </div>
        </div>
      </main>
    </>
  );
};

export default Milestones;
